import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminSMSDashboard() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "blocked" | "active">("all");
  const [sortBy, setSortBy] = useState<"sentCount" | "cost" | "status">("sentCount");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [blockReason, setBlockReason] = useState("");
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);

  // Fetch SMS stats
  const { data: statsData, isLoading: isLoadingStats } = trpc.admin.smsManagement.getAllSMSStats.useQuery({
    page,
    limit,
    search,
    status,
    sortBy,
  });

  // Fetch user details
  const { data: userDetails, isLoading: isLoadingDetails } = trpc.admin.smsManagement.getUserSMSDetails.useQuery(
    { userId: selectedUserId! },
    { enabled: !!selectedUserId }
  );

  // Mutations
  const blockUserMutation = trpc.admin.smsManagement.blockUserSMS.useMutation({
    onSuccess: () => {
      toast.success("User blocked successfully");
      setIsBlockDialogOpen(false);
      setBlockReason("");
      setSelectedUserId(null);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to block user");
    },
  });

  const unblockUserMutation = trpc.admin.smsManagement.unblockUserSMS.useMutation({
    onSuccess: () => {
      toast.success("User unblocked successfully");
      setSelectedUserId(null);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to unblock user");
    },
  });

  const resetCountersMutation = trpc.admin.smsManagement.resetUserSMSCounters.useMutation({
    onSuccess: () => {
      toast.success("Counters reset successfully");
      setSelectedUserId(null);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to reset counters");
    },
  });

  const handleBlock = () => {
    if (!selectedUserId || !blockReason.trim()) {
      toast.error("Please provide a block reason");
      return;
    }
    blockUserMutation.mutate({ userId: selectedUserId, reason: blockReason });
  };

  const handleUnblock = () => {
    if (!selectedUserId) return;
    unblockUserMutation.mutate({ userId: selectedUserId });
  };

  const handleResetCounters = () => {
    if (!selectedUserId) return;
    resetCountersMutation.mutate({ userId: selectedUserId, counters: "all" });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">SMS Rate Limiting Dashboard</h1>
        <p className="text-muted-foreground mt-2">Monitor and manage SMS usage and rate limits</p>
      </div>

      {/* Overview Stats */}
      {statsData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsData.overview.totalUsers}</div>
              <p className="text-xs text-muted-foreground">With SMS enabled</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Blocked Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{statsData.overview.blockedUsers}</div>
              <p className="text-xs text-muted-foreground">Currently blocked</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${statsData.overview.totalCostUSD.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Monthly Spend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${statsData.overview.monthlySpendUSD.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Current month</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              placeholder="Search by user ID..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />

            <Select value={status} onValueChange={(value: any) => {
              setStatus(value);
              setPage(1);
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(value: any) => {
              setSortBy(value);
              setPage(1);
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sentCount">Sort by Sent Count</SelectItem>
                <SelectItem value="cost">Sort by Cost</SelectItem>
                <SelectItem value="status">Sort by Status</SelectItem>
              </SelectContent>
            </Select>

            <Select value={limit.toString()} onValueChange={(value) => {
              setLimit(parseInt(value));
              setPage(1);
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="20">20 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>SMS Users</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingStats ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin" />
            </div>
          ) : statsData?.users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No users found
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User ID</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Blocked</TableHead>
                      <TableHead>Cost (USD)</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statsData?.users.map((user: any) => (
                      <TableRow key={user.userId}>
                        <TableCell className="font-mono">{user.userId}</TableCell>
                        <TableCell>{user.sentCount}</TableCell>
                        <TableCell>{user.blockedCount}</TableCell>
                        <TableCell>${user.totalCostUSD.toFixed(4)}</TableCell>
                        <TableCell>
                          <Badge variant={user.isBlocked ? "destructive" : "default"}>
                            {user.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Dialog open={selectedUserId === user.userId && isBlockDialogOpen} onOpenChange={setIsBlockDialogOpen}>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedUserId(user.userId)}
                              >
                                View
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>User SMS Details - ID {selectedUserId}</DialogTitle>
                              </DialogHeader>

                              {isLoadingDetails ? (
                                <div className="flex justify-center py-8">
                                  <Loader2 className="animate-spin" />
                                </div>
                              ) : userDetails ? (
                                <div className="space-y-6">
                                  {/* Stats */}
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-sm text-muted-foreground">Sent SMS</p>
                                      <p className="text-2xl font-bold">{userDetails.sentCount}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">Blocked SMS</p>
                                      <p className="text-2xl font-bold text-red-600">{userDetails.blockedCount}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">Total Cost</p>
                                      <p className="text-2xl font-bold">${userDetails.totalCostUSD.toFixed(2)}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">Monthly Cost</p>
                                      <p className="text-2xl font-bold">${userDetails.monthlyCostUSD.toFixed(2)}</p>
                                    </div>
                                  </div>

                                  {/* Current Counters */}
                                  <div className="border rounded-lg p-4">
                                    <h3 className="font-semibold mb-3">Current Counters</h3>
                                    <div className="grid grid-cols-3 gap-4">
                                      <div>
                                        <p className="text-sm text-muted-foreground">Hourly</p>
                                        <p className="text-lg font-bold">{userDetails.hourlyCount}/5</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-muted-foreground">Daily</p>
                                        <p className="text-lg font-bold">{userDetails.dailyCount}/20</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-muted-foreground">Monthly</p>
                                        <p className="text-lg font-bold">{userDetails.monthlyCount}/200</p>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Status */}
                                  <div className="border rounded-lg p-4">
                                    <h3 className="font-semibold mb-3">Status</h3>
                                    <div className="flex items-center gap-2">
                                      {userDetails.isBlocked ? (
                                        <>
                                          <XCircle className="text-red-600" />
                                          <span>Blocked: {userDetails.blockReason}</span>
                                        </>
                                      ) : (
                                        <>
                                          <CheckCircle2 className="text-green-600" />
                                          <span>Active</span>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="space-y-3">
                                    {userDetails.isBlocked ? (
                                      <Button
                                        onClick={handleUnblock}
                                        disabled={unblockUserMutation.isPending}
                                        className="w-full"
                                      >
                                        {unblockUserMutation.isPending ? (
                                          <>
                                            <Loader2 className="mr-2 animate-spin" />
                                            Unblocking...
                                          </>
                                        ) : (
                                          "Unblock User"
                                        )}
                                      </Button>
                                    ) : (
                                      <div className="space-y-2">
                                        <Input
                                          placeholder="Block reason..."
                                          value={blockReason}
                                          onChange={(e) => setBlockReason(e.target.value)}
                                        />
                                        <Button
                                          onClick={handleBlock}
                                          disabled={blockUserMutation.isPending || !blockReason.trim()}
                                          variant="destructive"
                                          className="w-full"
                                        >
                                          {blockUserMutation.isPending ? (
                                            <>
                                              <Loader2 className="mr-2 animate-spin" />
                                              Blocking...
                                            </>
                                          ) : (
                                            "Block User"
                                          )}
                                        </Button>
                                      </div>
                                    )}

                                    <Button
                                      onClick={handleResetCounters}
                                      disabled={resetCountersMutation.isPending}
                                      variant="outline"
                                      className="w-full"
                                    >
                                      {resetCountersMutation.isPending ? (
                                        <>
                                          <Loader2 className="mr-2 animate-spin" />
                                          Resetting...
                                        </>
                                      ) : (
                                        "Reset All Counters"
                                      )}
                                    </Button>
                                  </div>

                                  {/* Recent Audit Logs */}
                                  {userDetails.auditLogs.length > 0 && (
                                    <div className="border rounded-lg p-4">
                                      <h3 className="font-semibold mb-3">Recent Activity</h3>
                                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                        {userDetails.auditLogs.map((log: any, idx: number) => (
                                          <div key={idx} className="text-sm flex justify-between items-center gap-2">
                                            <span className="text-muted-foreground">
                                              {new Date(log.createdAt).toLocaleString()}
                                            </span>
                                            <div className="flex gap-2">
                                              <Badge variant={
                                                log.status === "sent" ? "default" :
                                                log.status === "blocked" ? "destructive" :
                                                "secondary"
                                              }>
                                                {log.status}
                                              </Badge>
                                              {log.deliveryStatus && (
                                                <Badge variant={
                                                  log.deliveryStatus === "delivered" ? "default" :
                                                  log.deliveryStatus === "failed" || log.deliveryStatus === "undelivered" ? "destructive" :
                                                  "secondary"
                                                }>
                                                  {log.deliveryStatus}
                                                </Badge>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                  Failed to load user details
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {statsData && (
                <div className="mt-4 flex justify-between items-center">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, statsData.pagination.total)} of {statsData.pagination.total}
                  </p>
                  <div className="space-x-2">
                    <Button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      variant="outline"
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => setPage(page + 1)}
                      disabled={page * limit >= statsData.pagination.total}
                      variant="outline"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
